import { get, post } from "../../../services/api";
import { WoApiError, WoCommandResult, WoContext, WoDetail, WoList } from "./types";

/** Thin wrappers over services/api (which attaches the session token). */
const payloadOf = <T,>(response: any): T => response.payload as T;
const id = (value: string) => encodeURIComponent(value);

/** Every failure becomes { status, code, message, details } the screens can branch on. */
export const toWoError = (error: any): WoApiError => {
  const status = Number(error?.response?.status) || 0;
  const payload = error?.response?.data?.payload || {};
  return {
    status,
    code: String(payload.code || (status ? "HTTP_" + status : "NETWORK")),
    message: String(
      payload.error ||
        error?.response?.data?.messages ||
        (status ? "Something went wrong. Please try again." : "No connection. Check the Wi-Fi and try again.")
    ),
    details: payload.details,
  };
};

const call = async <T,>(request: Promise<any>): Promise<T> => {
  try {
    return payloadOf<T>(await request);
  } catch (error) {
    throw toWoError(error);
  }
};

export const workOrdersApi = {
  context: () => call<WoContext>(get("work-orders/context")),
  list: (params: { store_id: number; view?: string; q?: string; page?: number; limit?: number }) => {
    const query = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join("&");
    return call<WoList>(get(`work-orders?${query}`));
  },
  get: (publicId: string) => call<WoDetail>(get(`work-orders/${id(publicId)}`)),
  signature: (publicId: string, signatureId: string) =>
    call<{ png_base64: string; width: number; height: number; kind: string }>(get(`work-orders/${id(publicId)}/signatures/${id(signatureId)}`)),
  create: (body: any) => call<WoCommandResult>(post("work-orders", body)),
  markReady: (publicId: string, body: any) => call<WoCommandResult>(post(`work-orders/${id(publicId)}/ready`, body)),
  reopen: (publicId: string, body: any) => call<WoCommandResult>(post(`work-orders/${id(publicId)}/reopen`, body)),
  cancel: (publicId: string, body: any) => call<WoCommandResult>(post(`work-orders/${id(publicId)}/cancel`, body)),
  pickup: (publicId: string, body: any) => call<WoCommandResult>(post(`work-orders/${id(publicId)}/pickup`, body)),
  edit: (publicId: string, body: any) => call<WoCommandResult>(post(`work-orders/${id(publicId)}/edit`, body)),
  addNote: (publicId: string, body: any) => call<WoCommandResult>(post(`work-orders/${id(publicId)}/notes`, body)),
  resend: (publicId: string, emailId: string, body: any) =>
    call<WoCommandResult>(post(`work-orders/${id(publicId)}/notifications/${id(emailId)}/resend`, body)),
};

export type WorkOrdersApi = typeof workOrdersApi;

/** A fresh request key for one action (reused unchanged if that action is retried). */
export const newRequestKey = (): string => {
  const randomUuid = (window.crypto as any)?.randomUUID;
  if (randomUuid) return randomUuid.call(window.crypto);
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};
